// Precarga de las web fonts (Google Fonts) usadas por el juego ANTES de arrancar
// Phaser. Por qué importa:
//
// Phaser `Text` rasteriza el string a un canvas UNA sola vez, al crear el objeto, y
// NO lo vuelve a dibujar cuando la web font termina de bajar. El <link display=swap>
// de index.html muestra primero una fuente de fallback, así que cualquier label
// creado antes de que llegue la fuente real queda "horneado" con el fallback (se ve
// distinto a los labels creados después). Eso producía que el nombre del propio
// jugador (creado al spawnear) y los nombres de NPCs (creados al entrar en pantalla)
// salieran con tipografías distintas.
//
// Solución: disparar la descarga al inicio del boot (no bloquea: baja durante el
// login / carga de assets) y `await fontsReady` justo antes de `new Phaser.Game`,
// igual que se hace con `assetsReady`. Así cuando aparece la pantalla la fuente ya
// está, y todos los labels nacen con la tipografía correcta.

import { AVAILABLE_PLAYER_NAMES_FONTS, GAME_FONT_FAMILY } from '@shared/constants';

// Specs que el juego usa al crear textos. Hay que pedir explícitamente cada
// combinación familia+peso+tamaño porque document.fonts.load resuelve por spec CSS.
// - Nombres de personajes/NPCs: `addPlayerNameText` usa bold 12px.
// - Resto de textos del juego: GAME_FONT_FAMILY (Montserrat) en varios pesos.
const FONT_SPECS: readonly string[] = [
  // Familias de nombres de personajes (Nunito Sans, Montserrat) en bold 12px.
  ...AVAILABLE_PLAYER_NAMES_FONTS.map((f) => `bold 12px ${f.family}`),
  // Fuente general del juego en los pesos declarados en index.html (400/600/700).
  // El 400 (regular) es el peso por default de addText/withGameFont (consola,
  // globos de chat, errores rojos): sin esta face cargada, el browser sustituía
  // con la 600 y todo texto no-bold se veía artificialmente pesado.
  `400 16px ${GAME_FONT_FAMILY}`,
  `600 16px ${GAME_FONT_FAMILY}`,
  `700 16px ${GAME_FONT_FAMILY}`,
  // Monogram (pixel font local, @font-face en index.html): tipografía de todo el
  // texto in-game. GAME_FONT_FAMILY ya la referencia, pero pedimos también el spec
  // crudo por tamaño para forzar su descarga antes de que Phaser hornee los labels.
  `12px monogram`,
  `16px monogram`,
  // Pixeloid: fuente del nombre de entidades/personajes.
  `12px pixeloid`,
];

/**
 * Dispara la descarga de todas las web fonts del juego y resuelve cuando están
 * listas para usarse (o cuando vence el timeout de cortesía, para no bloquear el
 * boot indefinidamente si Google Fonts no responde). NO bloquea al llamarse: se
 * invoca al inicio del boot y se espera con `await` recién antes de arrancar Phaser.
 *
 * @param timeoutMs Tope de espera. Pasado ese tiempo arrancamos igual (los labels
 *   podrían nacer con fallback, pero preferimos un juego que arranca a uno colgado).
 */
export function preloadGameFonts(timeoutMs = 4000): Promise<void> {
  // Navegadores sin CSS Font Loading API: no hay nada que esperar, seguimos.
  if (typeof document === 'undefined' || !document.fonts || !document.fonts.load) {
    return Promise.resolve();
  }

  const loadAll = Promise.all(
    FONT_SPECS.map((spec) =>
      // load() puede rechazar si la familia no existe todavía; lo absorbemos para
      // que un spec problemático no tumbe la precarga del resto.
      document.fonts.load(spec).catch(() => undefined)
    )
  ).then(() => undefined);

  const timeout = new Promise<void>((res) => {
    setTimeout(res, timeoutMs);
  });

  // Lo que pase primero: fuentes listas o timeout de cortesía.
  return Promise.race([loadAll, timeout]);
}
